import { objectsSummary } from "@/common/api/summary.api";
import { ListResult, POPULATION_TYPE } from "@/common/models";
import {
  ClusterSummary,
  FileSummary,
} from "@/common/models/entity-summary.model";
import { SharedState } from "@/common/state";
import {
  CoordinateInputTier3,
  ExternalEventType,
  JustificationStatus,
  MarkedStatus,
} from "@/gql/types";
import { Ref, ref } from "@vue/composition-api";
import moment from "moment";

import * as api from "./cluster.api";
import { ApiResult } from "@/common/api/ApiResult.model";

interface ClusterEventsFilters {
  mark?: MarkedStatus | null;
  justification?: JustificationStatus | null;
  externalEventTypes?: ExternalEventType[] | null;
  fromDate?: string | null;
  toDate?: string | null;
}

interface UseClusterOptions {
  eventsFilters?: () => ClusterEventsFilters;
}

export const UseCluster = (
  clusterUID: string,
  alt_coordinates: (time_frame: string) => CoordinateInputTier3[],
  options: UseClusterOptions = {}
) => {
  const cluster_id = ref("");
  const cluster_label: Ref<{ id: string; name: string } | null> = ref(null);
  const time_frames: Ref<string[]> = ref([]);
  const first_cluster_time = ref("");
  const last_cluster_time = ref("");
  const is_loading = ref(false);

  const cluster_files: Ref<any[]> = ref([]);
  const cluster_events: Ref<any[]> = ref([]);

  //#region common

  const get_first_and_last_time = (
    summary: ClusterSummary
  ): [string, string] => {
    const { firstExposer, lastExposer } = summary;
    return [firstExposer.exposureTime, lastExposer.exposureTime];
  };

  const date_format =
    (f = "yyyy-MM-DD") =>
    (the_date: string) => {
      return moment(the_date).format(f);
    };

  const yyyy_mm_dd = date_format("yyyy-MM-DD");
  const first_of_month = date_format("yyyy-MM-01");

  const set_time_frames = (first_time: string, last_time: string) => {
    const first_frame = yyyy_mm_dd(first_time);
    const last_frame = yyyy_mm_dd(last_time);

    first_cluster_time.value = first_frame;
    last_cluster_time.value = last_frame;

    const from = first_of_month(first_frame);
    const to = first_of_month(last_frame);

    const dates = [from];
    let current = from;
    while (moment(current).isBefore(to)) {
      current = moment(current).add(1, "month").format("yyyy-MM-01");
      dates.push(current);
    }
    console.log("dates", dates);
    return dates;
  };

  // procedural
  const init_cluster_data = async () => {
    const res = await objectsSummary({
      ids: [clusterUID],
      type: "clusters",
    });
    if (res.success) {
      const [first_time_frame, last_time_frame] = get_first_and_last_time(
        res.value[0] as ClusterSummary
      );
      const { id } = res.value[0] as ClusterSummary;
      cluster_id.value = id;
      const cluster_label_res = await api.get_cluster_label(cluster_id.value);
      if (cluster_label_res.success && cluster_label_res.value) {
        const cluster_label_id = cluster_label_res.value;
        const { labelName, id } =
          SharedState.allInfoTypes.value[cluster_label_id];
        cluster_label.value = { id, name: labelName };
      } else {
        cluster_label.value = null;
      }
      time_frames.value = set_time_frames(first_time_frame, last_time_frame);
    }
  };

  //#endregion

  //#region evets

  // procedural
  const _load_cluster_events = async (): Promise<ListResult<any>> => {
    let results: any[] = [];

    for (let i = 0; i < time_frames.value.length; i++) {
      let objects_count = 1;
      let cursor = "";
      const current_time_frame = time_frames.value[i];
      console.log(
        "load_cluster_events",
        "get_cluster_events , inside loop : current_time_frame = ",
        current_time_frame
      );
      while (objects_count > 0) {
        const filters = options.eventsFilters?.();
        const res = await api.getEventsForCluster({
          cords: alt_coordinates(current_time_frame),
          cursor,
          clusterUID: cluster_id.value,
          ...(filters?.mark ? { mark: filters.mark } : {}),
          ...(filters?.justification
            ? { justification: filters.justification }
            : {}),
          ...(filters?.externalEventTypes?.length
            ? { externalEventTypes: filters.externalEventTypes }
            : {}),
          ...(filters?.fromDate ? { fromDate: filters.fromDate } : {}),
          ...(filters?.toDate ? { toDate: filters.toDate } : {}),
        });
        if (res.success) {
          objects_count = res.value.objects.length;
          cursor = res.value.cursor;
          results = [...results, ...res.value.objects];
        } else {
          console.log("error in get_cluster_events : ", res.code);
          cursor = "";
          objects_count = 0;
          return {
            objects: [],
            cursor: "",
          };
        }
      }
    }
    return {
      objects: results,
      cursor: "",
    };
  };

  const load_cluster_events = async (): Promise<ApiResult<ListResult<any>>> => {
    console.log("load_cluster_events", "start");
    is_loading.value = true;
    await init_cluster_data();
    const res = await _load_cluster_events();
    console.log("load_cluster_events", "res", res.objects);
    is_loading.value = false;
    cluster_events.value = res.objects;
    return {
      success: true,
      value: {
        objects: cluster_events.value,
        cursor: "",
      },
    };
  };

  return {
    init_cluster_data,
    cluster_label,
    first_cluster_time,
    last_cluster_time,
    load_cluster_events,
    events: cluster_events,
    files: cluster_files,
  };
};
